import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import cookies from 'js-cookie';
import { Socket } from 'socket.io-client';
import { NotificationInstanceRecordResponse } from '@/shared/api';
import { NotificationType, StorageKeys } from '@/shared/types';
import { captureMessage } from '@/shared/utils/error-reporting';

type SocketNotification = {
  type: NotificationType;
  instance: NotificationInstanceRecordResponse;
};

export const useSocketGlobalListener = (socket?: Socket) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket) return;

    const onNotificationReceived = (notification: SocketNotification) => {
      switch (notification.type) {
        case NotificationType.UserAddedComment:
        case NotificationType.WorkspaceMemberAddedComment:
        case NotificationType.UserCompletedProc: {
          const activeOrgId = cookies.get(StorageKeys.ActiveOrganization);
          // refetch global notifications
          queryClient.invalidateQueries([
            'globalNotificationsGetPaginated',
            activeOrgId,
          ]);
          // refetch global notifications count
          queryClient.invalidateQueries(['notifications', activeOrgId, 'info']);

          const instanceId = location.pathname.split('/').pop();
          if (notification.instance.id === instanceId) {
            // refetch notifications for this instance
            queryClient.invalidateQueries([
              'authorInstanceNotificationsGetPaginated',
              instanceId,
            ]);
            queryClient.invalidateQueries([
              'userInstanceNotificationsGetPaginated',
              instanceId,
            ]);
            // refetch notifications count for this instance
            queryClient.invalidateQueries([
              'notifications',
              'instance',
              'user',
              instanceId,
              'info',
            ]);
            queryClient.invalidateQueries([
              'notifications',
              'instance',
              'author',
              instanceId,
              'info',
            ]);
            // refetch this instance
            //user instance
            queryClient.invalidateQueries(['instances', instanceId]);
            // author instance
            queryClient.invalidateQueries([
              'procs',
              'author',
              'instances',
              instanceId,
            ]);

            if (notification.instance?.step?.id) {
              // refetch comments for step
              queryClient.invalidateQueries([
                'agentMessagesGetPaginated',
                instanceId,
                notification.instance.step.id,
              ]);
            }
          }
          break;
        }
        default:
          captureMessage('Unknown socket notification type', 'warning', {
            notificationType: notification.type,
          });
      }
    };

    const onSubscriptionReceived = (subscription: unknown) => {
      captureMessage(`Subscription received, ${subscription}`);
      const activeOrgId = cookies.get(StorageKeys.ActiveOrganization);

      queryClient.invalidateQueries(['accounts', 'my-profile']);
      queryClient.invalidateQueries([
        'user-account',
        'organization',
        activeOrgId,
        'plan-usage',
      ]);
    };

    const onSubscriptionUpdated = (data: unknown) => {
      captureMessage(`Subscription updated, ${data}`);
    };

    // Set up listeners
    socket.on('notifications', onNotificationReceived);
    socket.on('subscriptions', onSubscriptionReceived);
    socket.on('subscription.updated', onSubscriptionUpdated);

    // Cleanup listeners
    return () => {
      socket.off('notifications', onNotificationReceived);
      socket.off('subscriptions', onSubscriptionReceived);
      socket.off('subscription.updated', onSubscriptionUpdated);
    };
  }, [socket, queryClient]);
};
